import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Container, Form } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import {
  FaArrowLeft,
  FaArrowRight,
  FaBoxOpen,
  FaBoxes,
  FaCheckCircle,
  FaClipboardList,
  FaEdit,
  FaImage,
  FaListUl,
  FaPlus,
  FaRedoAlt,
  FaSave,
  FaSearch,
  FaTags,
  FaTrash,
  FaWarehouse,
} from 'react-icons/fa';
import Navigation from './Navigation';
import AdminPageHeader from './AdminPageHeader';
import AdminDataTable from './AdminDataTable';
import useAuth from '../../hooks/useAuth';
import { isManagementUser, readStoredAdminUser } from './adminAccessControl';
import {
  POS_API_BASE_URL,
  POS_PRODUCT_ITEM_TYPE_OPTIONS,
  buildApiErrorMessage,
  buildAuthHeaders,
  buildPosImageUrl,
  fetchScopedOutlets,
  formatCurrency,
  getBooleanStatusClass,
  getInitialScopedOutlets,
  getInitials,
  getItemTypeBadgeClass,
  getItemTypeLabel,
  normalizePagedRecords,
  parseJsonResponse,
  resolveAdminSessionToken,
} from './posBackofficeHelpers';
import './PosCatalogManager.css';

const createEmptyProductForm = () => ({
  category_id: '',
  code: '',
  name: '',
  item_type: 'resale_item',
  price: '',
  last_purchase_price: '',
  stock: '',
  stock_reference_qty: '',
  satuan: '',
  purchase_unit: '',
  purchase_conversion_qty: '1',
  is_active: true,
});

const PRODUCT_MODE_GUIDES = {
  resale_item: {
    key: 'resale_item',
    eyebrow: 'Mode 1',
    title: 'POS Jual Langsung',
    icon: FaBoxes,
    description: 'Produk siap jual yang stoknya berkurang langsung dari product ini saat transaksi kasir.',
    highlights: [
      { label: 'Dipakai kasir', value: 'Muncul langsung di POS modern.' },
      { label: 'Mutasi stok', value: 'Stok bertambah dari purchase dan berkurang saat sale/refund.' },
      { label: 'Contoh', value: 'Minuman botol, snack, merchandise.' },
    ],
  },
  raw_material: {
    key: 'raw_material',
    eyebrow: 'Mode bahan',
    title: 'Bahan Baku',
    icon: FaWarehouse,
    description: 'Bahan yang tidak dijual langsung ke customer, tetapi dipakai oleh produk finished good melalui BOM.',
    highlights: [
      { label: 'Dipakai kasir', value: 'Tidak dijual langsung kecuali dibuat product lain.' },
      { label: 'Mutasi stok', value: 'Masuk dari purchase lalu dikonsumsi lewat recipe/BOM.' },
      { label: 'Contoh', value: 'Bubuk minuman, cup, packaging, bahan campuran.' },
    ],
  },
  finished_good: {
    key: 'finished_good',
    eyebrow: 'Mode 2',
    title: 'POS Dengan Bahan Baku',
    icon: FaBoxOpen,
    description: 'Produk jual yang stok atau biaya konsumsinya diturunkan dari bahan baku melalui recipe/BOM aktif.',
    highlights: [
      { label: 'Dipakai kasir', value: 'Muncul di POS dan mengonsumsi bahan sesuai BOM.' },
      { label: 'Mutasi stok', value: 'Perlu recipe aktif agar konsumsi bahan tercatat rapi.' },
      { label: 'Contoh', value: 'Menu racikan, paket minuman, makanan siap saji.' },
    ],
  },
  service: {
    key: 'service',
    eyebrow: 'Mode 3',
    title: 'Laundry / Jasa',
    icon: FaTags,
    description: 'Produk jasa tanpa stok product. Lanjutan operasionalnya dikelola dari Laundry Variants dan reference laundry.',
    highlights: [
      { label: 'Dipakai kasir', value: 'Dipakai untuk layanan jasa dan workflow laundry.' },
      { label: 'Mutasi stok', value: 'Tidak memakai stok product langsung.' },
      { label: 'Contoh', value: 'Cuci kiloan, express laundry, jasa setrika.' },
    ],
  },
};

const PRODUCT_FORM_STEPS = [
  {
    key: 'mode',
    badge: 'Langkah 1',
    title: 'Mode produk',
    helper: 'Pilih model operasional yang paling sesuai.',
  },
  {
    key: 'identity',
    badge: 'Langkah 2',
    title: 'Identitas',
    helper: 'Isi kategori, kode, dan nama produk.',
  },
  {
    key: 'pricing',
    badge: 'Langkah 3',
    title: 'Harga & stok',
    helper: 'Atur harga jual, HPP, stok, dan status.',
  },
  {
    key: 'media',
    badge: 'Langkah 4',
    title: 'Media & preview',
    helper: 'Tambahkan gambar dan cek hasil akhir.',
  },
];

const createEmptyRecipeDraft = () => ({
  ingredient_product_id: '',
  quantity_required: '',
  note: '',
});

const isRecipeEnabledItemType = itemType =>
  String(itemType || '').toLowerCase() === 'finished_good';

const isRecipeIngredientType = itemType =>
  ['raw_material', 'resale_item'].includes(String(itemType || '').toLowerCase());

const normalizeRecipeDrafts = items =>
  items
    .filter(item => String(item?.ingredient_product_id || '').trim() && String(item?.quantity_required || '').trim())
    .map(item => ({
      ingredient_product_id: Number(item.ingredient_product_id),
      quantity_required: Number(item.quantity_required),
      note: String(item.note || '').trim(),
    }))
    .filter(
      item =>
        Number.isFinite(item.ingredient_product_id) &&
        item.ingredient_product_id > 0 &&
        Number.isFinite(item.quantity_required) &&
        item.quantity_required > 0
    );

const formatInventoryCost = value =>
  `Rp ${Number(value || 0).toLocaleString('id-ID', {
    minimumFractionDigits: Number(value || 0) % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;

const formatInventoryUnitLabel = value => String(value || '').trim().toUpperCase() || '-';

const parseFlexibleNumber = value => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  const normalized = String(value || '').trim().replace(',', '.');
  const number = Number(normalized);
  return Number.isFinite(number) ? number : 0;
};

const formatEditableNumber = value => {
  const number = parseFlexibleNumber(value);
  if (!Number.isFinite(number)) {
    return '';
  }

  const normalized = Number(number.toFixed(4));
  return Number.isInteger(normalized) ? String(normalized) : String(normalized);
};

const deriveRawMaterialInventoryValues = draft => {
  const conversionQty = Math.max(parseFlexibleNumber(draft.purchase_conversion_qty || 1), 1);
  const hasPurchasePrice = String(draft.price || '').trim() !== '';
  const hasReferenceStock = String(draft.stock_reference_qty || '').trim() !== '';
  const totalPurchasePrice = parseFlexibleNumber(draft.price || 0);
  const referenceStockQty = parseFlexibleNumber(draft.stock_reference_qty || 0);
  const totalBaseStock = hasReferenceStock ? referenceStockQty * conversionQty : conversionQty;

  return {
    ...draft,
    last_purchase_price:
      hasPurchasePrice && totalBaseStock > 0 ? formatEditableNumber(totalPurchasePrice / totalBaseStock) : '',
    stock: hasReferenceStock ? formatEditableNumber(referenceStockQty * conversionQty) : '',
  };
};

function PosProductsManager() {
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyAndRefreshToken } = useAuth();
  const currentUser = useMemo(() => readStoredAdminUser(), []);
  const canAccess = isManagementUser(currentUser);
  const queryOutletId = useMemo(() => new URLSearchParams(location.search).get('outlet_id') || '', [location.search]);
  const queryWorkspace = useMemo(() => new URLSearchParams(location.search).get('workspace') || '', [location.search]);
  const queryTab = useMemo(() => new URLSearchParams(location.search).get('tab') || '', [location.search]);
  const isRawMaterialWorkspace = queryWorkspace === 'raw_material';
  const resolvedQueryTab = isRawMaterialWorkspace
    ? queryTab === 'form'
      ? 'form'
      : 'list'
    : queryTab === 'list'
      ? 'list'
      : 'form';
  const initialOutlets = getInitialScopedOutlets(currentUser);

  const [authToken, setAuthToken] = useState('');
  const [outlets, setOutlets] = useState(initialOutlets);
  const [selectedOutlet, setSelectedOutlet] = useState(queryOutletId || initialOutlets[0]?.id || '');
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [recipeCounts, setRecipeCounts] = useState({});
  const [formData, setFormData] = useState(createEmptyProductForm);
  const [editingId, setEditingId] = useState('');
  const [recipeItems, setRecipeItems] = useState([]);
  const [persistedRecipeCount, setPersistedRecipeCount] = useState(0);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');
  const [currentImageFilename, setCurrentImageFilename] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [activeWorkspaceTab, setActiveWorkspaceTab] = useState(resolvedQueryTab);
  const [activeFormStep, setActiveFormStep] = useState(isRawMaterialWorkspace ? 'identity' : 'mode');

  const categoryMap = useMemo(
    () =>
      categories.reduce((result, category) => {
        result[String(category.id)] = category.category_name || '-';
        return result;
      }, {}),
    [categories]
  );

  const selectedOutletName = useMemo(
    () => outlets.find(outlet => outlet.id === selectedOutlet)?.name || 'Belum dipilih',
    [outlets, selectedOutlet]
  );

  const workspaceProducts = useMemo(() => {
    return products.filter(product => {
      const itemType = String(product.item_type || 'resale_item').toLowerCase();
      return isRawMaterialWorkspace ? itemType === 'raw_material' : itemType !== 'raw_material';
    });
  }, [isRawMaterialWorkspace, products]);

  const filteredProducts = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return workspaceProducts;
    }

    return workspaceProducts.filter(product =>
      [
        product.code,
        product.name,
        product.item_type,
        categoryMap[String(product.category_id)] || product.category_name,
      ].some(value => String(value || '').toLowerCase().includes(query))
    );
  }, [categoryMap, search, workspaceProducts]);

  const productSummary = useMemo(() => {
    const activeCount = workspaceProducts.filter(product => Boolean(product.is_active)).length;
    const lowStockCount = workspaceProducts.filter(product =>
      ['resale_item', 'raw_material'].includes(String(product.item_type || 'resale_item')) &&
      Number(product.stock || 0) <= 5
    ).length;
    const recipeEnabledCount = workspaceProducts.filter(
      product => Number(recipeCounts[String(product.id)] || 0) > 0
    ).length;

    return {
      total: workspaceProducts.length,
      active: activeCount,
      lowStock: lowStockCount,
      recipeEnabled: recipeEnabledCount,
    };
  }, [recipeCounts, workspaceProducts]);

  const selectedModeGuide = useMemo(
    () => PRODUCT_MODE_GUIDES[formData.item_type] || PRODUCT_MODE_GUIDES.resale_item,
    [formData.item_type]
  );
  const availableItemTypeOptions = useMemo(
    () =>
      isRawMaterialWorkspace
        ? POS_PRODUCT_ITEM_TYPE_OPTIONS.filter(option => option.value === 'raw_material')
        : POS_PRODUCT_ITEM_TYPE_OPTIONS.filter(option => option.value !== 'raw_material'),
    [isRawMaterialWorkspace]
  );
  const usesDerivedStock = useMemo(
    () => ['finished_good', 'service'].includes(String(formData.item_type || '').toLowerCase()),
    [formData.item_type]
  );
  const usesPurchasingConfig = useMemo(
    () => ['raw_material', 'resale_item'].includes(String(formData.item_type || '').toLowerCase()),
    [formData.item_type]
  );
  const ingredientOptions = useMemo(
    () =>
      products.filter(
        product =>
          String(product.id || '') !== String(editingId || '') &&
          isRecipeIngredientType(product.item_type)
      ),
    [editingId, products]
  );
  const normalizedRecipeDrafts = useMemo(() => normalizeRecipeDrafts(recipeItems), [recipeItems]);
  const activeRecipeDraftCount = normalizedRecipeDrafts.length;
  const recipeCostPreview = useMemo(
    () =>
      normalizedRecipeDrafts.reduce((sum, item) => {
        const ingredient = products.find(product => String(product.id) === String(item.ingredient_product_id));
        return sum + Number(ingredient?.last_purchase_price || 0) * Number(item.quantity_required || 0);
      }, 0),
    [normalizedRecipeDrafts, products]
  );
  const effectiveLastPurchasePrice = useMemo(
    () => (isRecipeEnabledItemType(formData.item_type) ? recipeCostPreview : Number(formData.last_purchase_price || 0)),
    [formData.item_type, formData.last_purchase_price, recipeCostPreview]
  );
  const purchaseConversionQty = useMemo(
    () => Math.max(parseFlexibleNumber(formData.purchase_conversion_qty || 1), 1),
    [formData.purchase_conversion_qty]
  );
  const resolvedPurchaseUnit = useMemo(
    () => String(formData.purchase_unit || formData.satuan || '').trim().toUpperCase(),
    [formData.purchase_unit, formData.satuan]
  );
  const formSteps = useMemo(() => {
    const baseSteps = isRawMaterialWorkspace
      ? PRODUCT_FORM_STEPS.filter(step => step.key !== 'mode')
      : PRODUCT_FORM_STEPS;

    return baseSteps.map((step, index) => ({
      ...step,
      badge: `Langkah ${index + 1}`,
    }));
  }, [isRawMaterialWorkspace]);
  const pageTitle = isRawMaterialWorkspace ? 'Bahan Baku' : 'Produk & Jasa';
  const pageDescription = isRawMaterialWorkspace
    ? 'Kelola master bahan baku secara terpisah agar user fokus pada stok bahan, satuan, dan pembelian.'
    : 'Satu master produk dipakai untuk POS jual langsung, produk dengan bahan baku, dan laundry/jasa.';
  const workspaceEntityLabelTitle = isRawMaterialWorkspace ? 'Bahan Baku' : 'Produk';
  const workspaceMenuItems = useMemo(
    () => [
      {
        key: 'product_list',
        label: 'Daftar Product',
        helper: 'Lihat, cari, dan edit product outlet.',
        icon: FaListUl,
        active: !isRawMaterialWorkspace && activeWorkspaceTab === 'list',
      },
      {
        key: 'raw_material',
        label: 'Bahan Baku',
        helper: 'Master stok bahan untuk BOM.',
        icon: FaWarehouse,
        active: isRawMaterialWorkspace,
      },
      {
        key: 'product_form',
        label: 'Input Product',
        helper: 'Isi data produk secara bertahap.',
        icon: FaClipboardList,
        active: !isRawMaterialWorkspace && activeWorkspaceTab === 'form',
      },
    ],
    [activeWorkspaceTab, isRawMaterialWorkspace]
  );

  const activeFormStepIndex = useMemo(
    () => Math.max(formSteps.findIndex(step => step.key === activeFormStep), 0),
    [activeFormStep, formSteps]
  );

  const activeFormStepConfig = formSteps[activeFormStepIndex] || formSteps[0];

  const resetForm = useCallback(() => {
    setEditingId('');
    setFormData({
      ...createEmptyProductForm(),
      item_type: isRawMaterialWorkspace ? 'raw_material' : 'resale_item',
    });
    setRecipeItems([]);
    setPersistedRecipeCount(0);
    setImageFile(null);
    setImagePreviewUrl('');
    setCurrentImageFilename('');
    setActiveWorkspaceTab(resolvedQueryTab);
    setActiveFormStep(isRawMaterialWorkspace ? 'identity' : 'mode');
  }, [isRawMaterialWorkspace, resolvedQueryTab]);

  const fetchCategories = useCallback(async (token, outletId) => {
    if (!outletId || !POS_API_BASE_URL) {
      setCategories([]);
      return;
    }

    const response = await fetch(
      `${POS_API_BASE_URL}api/categories/?outlet_id=${encodeURIComponent(outletId)}`,
      {
        headers: buildAuthHeaders(token),
      }
    );
    const data = await parseJsonResponse(response);
    if (!response.ok) {
      throw new Error(buildApiErrorMessage(response, data, 'Gagal memuat kategori produk.'));
    }

    const records = Array.isArray(data) ? data : [];
    setCategories(records.filter(category => category.status !== false));
  }, []);

  const fetchProducts = useCallback(async (token, outletId) => {
    if (!outletId || !POS_API_BASE_URL) {
      setProducts([]);
      setRecipeCounts({});
      return;
    }

    setLoading(true);
    setErrorMessage('');
    try {
      const [productResponse, recipeResponse] = await Promise.all([
        fetch(
          `${POS_API_BASE_URL}api/products/?outlet_id=${encodeURIComponent(outletId)}&limit=300&page=1`,
          { headers: buildAuthHeaders(token) }
        ),
        fetch(
          `${POS_API_BASE_URL}api/product-recipes/?outlet_id=${encodeURIComponent(outletId)}`,
          { headers: buildAuthHeaders(token) }
        ),
      ]);

      const productData = await parseJsonResponse(productResponse);
      const recipeData = await parseJsonResponse(recipeResponse);

      if (!productResponse.ok) {
        throw new Error(buildApiErrorMessage(productResponse, productData, 'Gagal memuat produk POS.'));
      }

      if (!recipeResponse.ok) {
        throw new Error(buildApiErrorMessage(recipeResponse, recipeData, 'Gagal memuat recipe/BOM produk.'));
      }

      const { records } = normalizePagedRecords(productData);
      setProducts(Array.isArray(records) ? records : []);

      const nextRecipeCounts = (Array.isArray(recipeData) ? recipeData : []).reduce((result, recipe) => {
        const key = String(recipe.product_id || '');
        if (!key) {
          return result;
        }
        result[key] = (result[key] || 0) + 1;
        return result;
      }, {});
      setRecipeCounts(nextRecipeCounts);
    } catch (error) {
      console.error('Failed to fetch POS products:', error);
      setProducts([]);
      setRecipeCounts({});
      setErrorMessage(error.message || 'Gagal memuat produk POS.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadProductDetail = useCallback(async productId => {
    const response = await fetch(`${POS_API_BASE_URL}api/products/${productId}`, {
      headers: buildAuthHeaders(authToken),
    });
    const data = await parseJsonResponse(response);
    if (!response.ok) {
      throw new Error(buildApiErrorMessage(response, data, 'Gagal memuat detail produk.'));
    }
    return data;
  }, [authToken]);

  const loadProductRecipes = useCallback(async productId => {
    if (!selectedOutlet) {
      return [];
    }

    const response = await fetch(
      `${POS_API_BASE_URL}api/product-recipes/?outlet_id=${encodeURIComponent(selectedOutlet)}&product_id=${encodeURIComponent(productId)}`,
      {
        headers: buildAuthHeaders(authToken),
      }
    );
    const data = await parseJsonResponse(response);
    if (!response.ok) {
      throw new Error(buildApiErrorMessage(response, data, 'Gagal memuat recipe/BOM produk.'));
    }

    const records = Array.isArray(data?.data) ? data.data : [];
    return records.map(item => ({
      ingredient_product_id: String(item.ingredient_product_id || ''),
      quantity_required: String(item.quantity_required || ''),
      note: item.note || '',
    }));
  }, [authToken, selectedOutlet]);

  useEffect(() => {
    if (!canAccess) {
      navigate('/dashboardadmin', { replace: true });
      return;
    }

    let active = true;

    const initialize = async () => {
      try {
        const token = await resolveAdminSessionToken(verifyAndRefreshToken, navigate);
        if (!active || !token) {
          return;
        }

        setAuthToken(token);
        const scopedOutlets = await fetchScopedOutlets({ token, currentUser });
        if (!active) {
          return;
        }

        setOutlets(scopedOutlets);
        setSelectedOutlet(prev => prev || queryOutletId || scopedOutlets[0]?.id || '');
      } catch (error) {
        console.error('Failed to initialize POS products page:', error);
        if (active) {
          setErrorMessage(error.message || 'Gagal menyiapkan halaman produk POS.');
        }
      }
    };

    initialize();
    return () => {
      active = false;
    };
  }, [canAccess, currentUser, navigate, queryOutletId, verifyAndRefreshToken]);

  useEffect(() => {
    if (!authToken || !selectedOutlet) {
      return;
    }

    fetchCategories(authToken, selectedOutlet).catch(error => {
      console.error('Failed to fetch POS product categories:', error);
      setErrorMessage(error.message || 'Gagal memuat kategori produk POS.');
    });
    fetchProducts(authToken, selectedOutlet);
  }, [authToken, fetchCategories, fetchProducts, selectedOutlet]);

  useEffect(() => {
    resetForm();
  }, [resetForm, selectedOutlet]);

  useEffect(() => {
    setActiveWorkspaceTab(resolvedQueryTab);
  }, [resolvedQueryTab]);

  useEffect(() => {
    if (formSteps.some(step => step.key === activeFormStep)) {
      return;
    }

    setActiveFormStep(formSteps[0]?.key || 'identity');
  }, [activeFormStep, formSteps]);

  useEffect(() => {
    if (!imageFile) {
      return undefined;
    }

    const objectUrl = URL.createObjectURL(imageFile);
    setImagePreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [imageFile]);

  const handleFormChange = event => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;

    setFormData(prev => {
      const nextState = {
        ...prev,
        [target.name]: value,
        ...(target.name === 'item_type' && ['finished_good', 'service'].includes(String(value || '').toLowerCase())
          ? { stock: '0' }
          : {}),
      };

      if (isRawMaterialWorkspace || String(nextState.item_type || '').toLowerCase() === 'raw_material') {
        return deriveRawMaterialInventoryValues(nextState);
      }

      return nextState;
    });
  };

  const handleRecipeRowChange = (index, field, value) => {
    setRecipeItems(prev =>
      prev.map((item, itemIndex) => (itemIndex === index ? { ...item, [field]: value } : item))
    );
  };

  const handleAddRecipeRow = () => {
    setRecipeItems(prev => [...prev, createEmptyRecipeDraft()]);
  };

  const handleRemoveRecipeRow = index => {
    setRecipeItems(prev => prev.filter((_, itemIndex) => itemIndex !== index));
  };

  const handleImageChange = event => {
    const nextFile = event.target.files?.[0] || null;
    setImageFile(nextFile);
    if (!nextFile && currentImageFilename) {
      setImagePreviewUrl(buildPosImageUrl(currentImageFilename));
    }
  };

  const startEdit = useCallback(async product => {
    try {
      const [detail, loadedRecipeItems] = await Promise.all([
        loadProductDetail(product.id),
        loadProductRecipes(product.id),
      ]);
      const nextItemType = String(detail.item_type || product.item_type || 'resale_item');
      const usesDerivedStockForDetail = ['finished_good', 'service'].includes(nextItemType.toLowerCase());
      const nextPurchaseConversionQty = Math.max(
        Number(detail.purchase_conversion_qty ?? product.purchase_conversion_qty ?? 1),
        1
      );
      const nextBaseFormData = {
        category_id: String(detail.category_id || product.category_id || ''),
        code: String(detail.code || product.code || ''),
        name: String(detail.name || product.name || ''),
        item_type: nextItemType,
        price: String(detail.price ?? product.price ?? ''),
        last_purchase_price: String(detail.last_purchase_price ?? product.last_purchase_price ?? ''),
        stock: usesDerivedStockForDetail ? '0' : String(detail.stock ?? product.stock ?? ''),
        stock_reference_qty:
          nextItemType.toLowerCase() === 'raw_material' && !usesDerivedStockForDetail
            ? formatEditableNumber(Number(detail.stock ?? product.stock ?? 0) / nextPurchaseConversionQty)
            : '',
        satuan: String(detail.satuan || product.satuan || ''),
        purchase_unit: String(detail.purchase_unit || product.purchase_unit || detail.satuan || product.satuan || ''),
        purchase_conversion_qty: String(nextPurchaseConversionQty),
        is_active: Boolean(detail.is_active ?? product.is_active),
      };
      setActiveWorkspaceTab('form');
      setActiveFormStep('identity');
      setEditingId(String(detail.id || product.id || ''));
      setFormData(
        nextItemType.toLowerCase() === 'raw_material'
          ? deriveRawMaterialInventoryValues(nextBaseFormData)
          : nextBaseFormData
      );
      setRecipeItems(loadedRecipeItems);
      setPersistedRecipeCount(loadedRecipeItems.length);
      setCurrentImageFilename(String(detail.image_url || product.image_url || ''));
      setImageFile(null);
      setImagePreviewUrl(buildPosImageUrl(detail.image_url || product.image_url || ''));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Failed to start POS product edit:', error);
      Swal.fire('Error', error.message || 'Gagal membuka detail produk.', 'error');
    }
  }, [loadProductDetail, loadProductRecipes]);

  const uploadImageIfNeeded = async () => {
    if (!imageFile) {
      return currentImageFilename;
    }

    const payload = new FormData();
    payload.append('image', imageFile);

    const response = await fetch(`${POS_API_BASE_URL}api/products/uploads`, {
      method: 'POST',
      headers: buildAuthHeaders(authToken),
      body: payload,
    });
    const data = await parseJsonResponse(response);
    if (!response.ok) {
      throw new Error(buildApiErrorMessage(response, data, 'Gagal mengunggah gambar produk.'));
    }

    return String(data?.filename || currentImageFilename || '');
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (!selectedOutlet) {
      Swal.fire('Error', 'Pilih outlet lebih dulu.', 'error');
      return;
    }

    const shouldUseRecipe = isRecipeEnabledItemType(formData.item_type);
    if (shouldUseRecipe && normalizedRecipeDrafts.length === 0) {
      Swal.fire('Error', 'Produk finished good wajib memiliki minimal 1 bahan recipe/BOM.', 'error');
      return;
    }

    setSaving(true);
    try {
      const uploadedFilename = await uploadImageIfNeeded();
      const payload = {
        outlet_id: Number(selectedOutlet),
        category_id: Number(formData.category_id),
        code: formData.code.trim().toUpperCase(),
        name: formData.name.trim(),
        item_type: formData.item_type,
        price: Number(formData.price || 0),
        last_purchase_price: effectiveLastPurchasePrice,
        stock: usesDerivedStock ? 0 : Number(formData.stock || 0),
        satuan: formData.satuan.trim().toUpperCase(),
        purchase_unit: String(formData.purchase_unit || formData.satuan || '').trim().toUpperCase(),
        purchase_conversion_qty: parseFlexibleNumber(formData.purchase_conversion_qty || 1),
        image_url: uploadedFilename,
        is_active: Boolean(formData.is_active),
      };

      const response = await fetch(
        editingId
          ? `${POS_API_BASE_URL}api/products/${editingId}`
          : `${POS_API_BASE_URL}api/products/`,
        {
          method: editingId ? 'PUT' : 'POST',
          headers: buildAuthHeaders(authToken, {
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify(payload),
        }
      );
      const data = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(buildApiErrorMessage(response, data, 'Gagal menyimpan produk POS.'));
      }

      const targetProductId = Number(data?.id || editingId || 0);

      if (shouldUseRecipe && targetProductId > 0) {
        const recipeResponse = await fetch(`${POS_API_BASE_URL}api/product-recipes/`, {
          method: 'POST',
          headers: buildAuthHeaders(authToken, {
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify({
            outlet_id: Number(selectedOutlet),
            product_id: targetProductId,
            items: normalizedRecipeDrafts,
          }),
        });
        const recipeData = await parseJsonResponse(recipeResponse);
        if (!recipeResponse.ok) {
          throw new Error(buildApiErrorMessage(recipeResponse, recipeData, 'Gagal menyimpan recipe/BOM produk.'));
        }
      } else if (!shouldUseRecipe && persistedRecipeCount > 0 && editingId) {
        const deleteResponse = await fetch(
          `${POS_API_BASE_URL}api/product-recipes/${editingId}?outlet_id=${encodeURIComponent(selectedOutlet)}`,
          {
            method: 'DELETE',
            headers: buildAuthHeaders(authToken),
          }
        );
        const deleteData = await parseJsonResponse(deleteResponse);
        if (!deleteResponse.ok) {
          throw new Error(buildApiErrorMessage(deleteResponse, deleteData, 'Gagal menghapus recipe/BOM lama.'));
        }
      }

      await fetchProducts(authToken, selectedOutlet);
      resetForm();
      setActiveWorkspaceTab('list');
      Swal.fire(
        'Success',
        editingId ? `${workspaceEntityLabelTitle} berhasil diperbarui.` : `${workspaceEntityLabelTitle} berhasil dibuat.`,
        'success'
      );
    } catch (error) {
      console.error('Failed to save POS product:', error);
      Swal.fire('Error', error.message || 'Gagal menyimpan produk POS.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = useCallback(async product => {
    const confirmed = await Swal.fire({
      title: 'Hapus produk?',
      text: `Produk ${product.name || product.code || ''} akan dihapus permanen.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Hapus',
      cancelButtonText: 'Batal',
      confirmButtonColor: '#d45463',
    });

    if (!confirmed.isConfirmed) {
      return;
    }

    try {
      const response = await fetch(`${POS_API_BASE_URL}api/products/${product.id}`, {
        method: 'DELETE',
        headers: buildAuthHeaders(authToken),
      });
      const data = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(buildApiErrorMessage(response, data, 'Gagal menghapus produk POS.'));
      }

      if (String(editingId) === String(product.id)) {
        resetForm();
      }
      await fetchProducts(authToken, selectedOutlet);
      Swal.fire('Success', `${workspaceEntityLabelTitle} berhasil dihapus.`, 'success');
    } catch (error) {
      console.error('Failed to delete POS product:', error);
      Swal.fire('Error', error.message || 'Gagal menghapus produk POS.', 'error');
    }
  }, [authToken, editingId, fetchProducts, resetForm, selectedOutlet, workspaceEntityLabelTitle]);

  const columns = useMemo(
    () => [
      {
        name: 'Product',
        sortable: true,
        grow: 1.6,
        cell: row => (
          <div className="table-identity">
            {row.image_url ? (
              <img
                src={buildPosImageUrl(row.image_url)}
                alt={row.name || row.code || 'Product'}
                className="pos-catalog-image"
              />
            ) : (
              <span className="table-identity__avatar">{getInitials(row.name || row.code || 'PR')}</span>
            )}
            <div className="table-identity__meta">
              <strong>{row.name || '-'}</strong>
              <small>Kode: {row.code || '-'} • {categoryMap[String(row.category_id)] || row.category_name || '-'}</small>
            </div>
          </div>
        ),
      },
      {
        name: 'Type',
        sortable: true,
        selector: row => row.item_type || 'resale_item',
        cell: row => (
          <div className="pos-catalog-value-stack">
            <span className={getItemTypeBadgeClass(row.item_type)}>{getItemTypeLabel(row.item_type)}</span>
            <small>{Number(recipeCounts[String(row.id)] || 0)} BOM aktif</small>
          </div>
        ),
      },
      {
        name: 'Pricing',
        sortable: true,
        grow: 1.15,
        cell: row => (
          <div className="pos-catalog-value-stack">
            <strong>
              {String(row.item_type || '').toLowerCase() === 'raw_material'
                ? `Total ${formatCurrency(row.price)}`
                : formatCurrency(row.price)}
            </strong>
            <small>
              {String(row.item_type || '').toLowerCase() === 'finished_good'
                ? `HPP BOM ${formatCurrency(row.last_purchase_price)}`
                : `HPP / ${formatInventoryUnitLabel(row.satuan)} ${formatCurrency(row.last_purchase_price)}`}
            </small>
          </div>
        ),
      },
      {
        name: 'Stock',
        sortable: true,
        selector: row => Number(row.stock || 0),
        cell: row => (
          <div className="pos-catalog-value-stack">
            {String(row.item_type || '').toLowerCase() === 'finished_good' ? (
              <>
                <strong>{Number(recipeCounts[String(row.id)] || 0)} bahan BOM</strong>
                <small>
                  {Number(recipeCounts[String(row.id)] || 0) > 0
                    ? 'Stok diturunkan dari bahan baku'
                    : 'BOM belum diset'}
                </small>
              </>
            ) : (
              <>
                <strong>
                  {Number(row.stock || 0).toLocaleString('id-ID')} {row.satuan || ''}
                </strong>
                <small>{Number(row.stock || 0) <= 5 ? 'Perlu perhatian' : 'Stok aman'}</small>
              </>
            )}
          </div>
        ),
      },
      {
        name: 'Status',
        sortable: true,
        selector: row => (row.is_active ? 'Active' : 'Inactive'),
        cell: row => (
          <span className={getBooleanStatusClass(Boolean(row.is_active))}>
            {row.is_active ? 'Active' : 'Inactive'}
          </span>
        ),
      },
      {
        name: 'Actions',
        width: '180px',
        cell: row => (
          <div className="table-action-group">
            <Button variant="outline-primary" size="sm" onClick={() => startEdit(row)}>
              <FaEdit /> Edit
            </Button>
            <Button variant="outline-danger" size="sm" onClick={() => handleDelete(row)}>
              <FaTrash /> Hapus
            </Button>
          </div>
        ),
      },
    ],
    [categoryMap, handleDelete, recipeCounts, startEdit]
  );

  const renderRecipeEditor = () => (
    <section className="pos-catalog-subsection">
      <div className="pos-catalog-section-head">
        <div>
          <span className="section-badge">Recipe / BOM</span>
          <h2>Takaran bahan per 1 produk jual</h2>
          <p>
            Contoh: Kopi Susu = 18 gram kopi + 20 gram gula + 150 ml air. HPP akan dihitung otomatis dari biaya dasar tiap bahan.
          </p>
        </div>
        <Button type="button" variant="outline-primary" onClick={handleAddRecipeRow}>
          <FaPlus /> Tambah bahan
        </Button>
      </div>

      <div className="pos-catalog-inline-list">
        <article className="pos-catalog-inline-item">
          <span>Bahan aktif</span>
          <strong>{activeRecipeDraftCount}</strong>
        </article>
        <article className="pos-catalog-inline-item">
          <span>HPP otomatis / porsi</span>
          <strong>{formatCurrency(recipeCostPreview)}</strong>
        </article>
      </div>

      <div className="pos-catalog-field-note">
        Gunakan satuan stok terkecil di bahan baku seperti `pcs`, `gram`, atau `ml`. Jika beli 1 KG kopi, stok bahan tetap simpan dalam `gram`, lalu isi recipe misalnya `18` untuk 18 gram per cup.
      </div>

      {ingredientOptions.length === 0 ? (
        <Alert variant="warning" className="mb-0">
          Belum ada product bertipe bahan baku atau jual langsung yang dapat dipakai sebagai ingredient.
        </Alert>
      ) : null}

      {recipeItems.length === 0 ? (
        <div className="pos-catalog-empty-state">
          Tambahkan minimal satu bahan untuk finished good ini.
        </div>
      ) : (
        <div className="pos-catalog-recipe-list">
          {recipeItems.map((item, index) => {
            const selectedIngredient = ingredientOptions.find(
              option => String(option.id) === String(item.ingredient_product_id)
            );
            const ingredientUnitCost = Number(selectedIngredient?.last_purchase_price || 0);
            const ingredientQty = Number(item.quantity_required || 0);
            const ingredientSubtotal = ingredientUnitCost * ingredientQty;

            return (
              <div key={`${item.ingredient_product_id}-${index}`} className="pos-catalog-recipe-row">
                <Form.Group className="field-stack span-5">
                  <Form.Label>Bahan</Form.Label>
                  <Form.Select
                    value={item.ingredient_product_id}
                    onChange={event => handleRecipeRowChange(index, 'ingredient_product_id', event.target.value)}
                  >
                    <option value="">Pilih bahan baku</option>
                    {ingredientOptions.map(option => (
                      <option key={option.id} value={option.id}>
                        {option.code} - {option.name} ({option.satuan || '-'})
                      </option>
                    ))}
                  </Form.Select>
                  <div className="pos-catalog-field-note">
                    {selectedIngredient
                      ? `Satuan stok bahan: ${formatInventoryUnitLabel(selectedIngredient.satuan)} • HPP dasar ${formatInventoryCost(ingredientUnitCost)} / ${formatInventoryUnitLabel(selectedIngredient.satuan)}`
                      : 'Pilih bahan untuk melihat satuan stok dan biaya dasar yang dipakai.'}
                  </div>
                </Form.Group>

                <Form.Group className="field-stack span-3">
                  <Form.Label>Takaran / qty</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    value={item.quantity_required}
                    onChange={event => handleRecipeRowChange(index, 'quantity_required', event.target.value)}
                    placeholder="0"
                  />
                  <div className="pos-catalog-field-note">
                    Per 1 produk jual{selectedIngredient?.satuan ? ` (${formatInventoryUnitLabel(selectedIngredient.satuan)})` : ''}.
                  </div>
                </Form.Group>

                <Form.Group className="field-stack span-3">
                  <Form.Label>Catatan</Form.Label>
                  <Form.Control
                    value={item.note}
                    onChange={event => handleRecipeRowChange(index, 'note', event.target.value)}
                    placeholder="Opsional"
                  />
                </Form.Group>

                <div className="field-stack span-1">
                  <Form.Label>&nbsp;</Form.Label>
                  <Button type="button" variant="outline-danger" onClick={() => handleRemoveRecipeRow(index)}>
                    <FaTrash />
                  </Button>
                </div>

                <div className="pos-catalog-recipe-row__meta">
                  <span>
                    {selectedIngredient
                      ? `Biaya bahan: ${formatInventoryCost(ingredientUnitCost)} x ${ingredientQty || 0} ${formatInventoryUnitLabel(selectedIngredient.satuan)}`
                      : 'Pilih bahan terlebih dulu untuk melihat kalkulasi.'}
                  </span>
                  <strong>{formatCurrency(ingredientSubtotal)}</strong>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );

  const navigateToWorkspace = useCallback((nextWorkspace, nextTab = resolvedQueryTab) => {
    const params = new URLSearchParams();
    if (selectedOutlet) {
      params.set('outlet_id', selectedOutlet);
    }
    if (nextWorkspace === 'raw_material') {
      params.set('workspace', 'raw_material');
    }
    params.set('tab', nextTab === 'list' ? 'list' : 'form');

    navigate(`/pos-products${params.toString() ? `?${params.toString()}` : ''}`);
  }, [navigate, resolvedQueryTab, selectedOutlet]);

  const openCreateRawMaterialForm = useCallback(() => {
    resetForm();
    navigateToWorkspace('raw_material', 'form');
  }, [navigateToWorkspace, resetForm]);

  const headerActions = (
    <>
      <div className="page-action-group">
        <Form.Select
          className="page-action-select"
          value={selectedOutlet}
          onChange={event => setSelectedOutlet(event.target.value)}
          disabled={outlets.length === 0}
        >
          {outlets.length === 0 ? <option value="">Belum ada outlet</option> : null}
          {outlets.map(outlet => (
            <option key={outlet.id} value={outlet.id}>
              {outlet.name}
            </option>
          ))}
        </Form.Select>
      </div>
      <Button
        variant="outline-primary"
        onClick={() => fetchProducts(authToken, selectedOutlet)}
        disabled={!authToken || !selectedOutlet || loading}
      >
        <FaRedoAlt /> Refresh
      </Button>
    </>
  );

  const headerMeta = (
    <>
      <span className="page-meta__chip">
        <small>{isRawMaterialWorkspace ? 'Total bahan' : 'Total produk'}</small>
        <strong>{productSummary.total}</strong>
      </span>
      <span className="page-meta__chip">
        <small>Outlet aktif</small>
        <strong>{selectedOutletName}</strong>
      </span>
    </>
  );

  const goToNextFormStep = () => {
    const nextIndex = Math.min(activeFormStepIndex + 1, formSteps.length - 1);
    setActiveFormStep(formSteps[nextIndex].key);
  };

  const goToPreviousFormStep = () => {
    const nextIndex = Math.max(activeFormStepIndex - 1, 0);
    setActiveFormStep(formSteps[nextIndex].key);
  };

  const renderFormStepContent = () => {
    if (activeFormStep === 'mode') {
      return (
        <>
          <section className="pos-catalog-form-section">
            <div className="pos-catalog-section-head">
              <div>
                <span className="section-badge">Langkah 1</span>
                <h2>Pilih mode produk</h2>
                <p>Tentukan dulu produk ini termasuk jual langsung, produk BOM, atau jasa laundry.</p>
              </div>
            </div>

            <div className="pos-catalog-mode-selector">
              {Object.values(PRODUCT_MODE_GUIDES)
                .filter(mode => mode.key !== 'raw_material')
                .map(mode => {
                const ModeIcon = mode.icon;
                const isActive = formData.item_type === mode.key;
                return (
                  <button
                    key={mode.key}
                    type="button"
                    className={`pos-catalog-mode-option${isActive ? ' is-active' : ''}`}
                    onClick={() =>
                      setFormData(prev => ({
                        ...prev,
                        item_type: mode.key,
                        ...(['finished_good', 'service'].includes(mode.key) ? { stock: '0' } : {}),
                      }))
                    }
                  >
                    <span className="pos-catalog-mode-option__icon">
                      <ModeIcon />
                    </span>
                    <div className="pos-catalog-value-stack">
                      <small>{mode.eyebrow}</small>
                      <strong>{mode.title}</strong>
                      <span>{mode.description}</span>
                    </div>
                  </button>
                );
                })}
            </div>
          </section>

          <section className="pos-catalog-subsection">
            <div className="pos-catalog-section-head">
              <div>
                <span className="section-badge">Mode aktif</span>
                <h2>{selectedModeGuide.title}</h2>
                <p>{selectedModeGuide.description}</p>
              </div>
              <span className={getItemTypeBadgeClass(formData.item_type)}>
                {getItemTypeLabel(formData.item_type)}
              </span>
            </div>

            <div className="pos-catalog-inline-list">
              {selectedModeGuide.highlights.map(item => (
                <article key={`${selectedModeGuide.key}-${item.label}`} className="pos-catalog-inline-item">
                  <div className="pos-catalog-value-stack">
                    <strong>{item.label}</strong>
                    <small>{item.value}</small>
                  </div>
                </article>
              ))}
            </div>
          </section>
        </>
      );
    }

    if (activeFormStep === 'identity') {
      return (
        <section className="pos-catalog-form-section">
          <div className="pos-catalog-section-head">
            <div>
              <span className="section-badge">
                {isRawMaterialWorkspace ? 'Langkah 1' : 'Langkah 2'}
              </span>
              <h2>{isRawMaterialWorkspace ? 'Identitas bahan baku' : 'Identitas produk'}</h2>
              <p>
                {isRawMaterialWorkspace
                  ? 'Isi kategori, kode, dan nama bahan baku untuk stok, pembelian, dan BOM.'
                  : 'Isi kategori, kode, dan nama produk yang akan tampil di kasir dan laporan.'}
              </p>
            </div>
          </div>

          {isRawMaterialWorkspace ? (
            <Form.Group className="field-stack mb-3">
              <Form.Label>Workspace item</Form.Label>
              <div className="pos-catalog-inline-item">
                <div className="pos-catalog-value-stack">
                  <strong>Bahan Baku</strong>
                  <small>
                    Tipe item dikunci ke `raw_material` di workspace ini supaya user tidak bingung memilih mode.
                  </small>
                </div>
              </div>
            </Form.Group>
          ) : null}

          <div className="form-grid">
            <Form.Group className="field-stack span-6">
              <Form.Label>Kategori</Form.Label>
              <Form.Select
                name="category_id"
                value={formData.category_id}
                onChange={handleFormChange}
                required
              >
                <option value="">Pilih kategori</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.category_name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {isRawMaterialWorkspace ? null : (
              <Form.Group className="field-stack span-6">
                <Form.Label>Tipe item</Form.Label>
                <Form.Select
                  name="item_type"
                  value={formData.item_type}
                  onChange={handleFormChange}
                >
                  {availableItemTypeOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
                <div className="pos-catalog-field-note">
                  Jika belum yakin, pilih mode visual di langkah pertama lalu biarkan field ini ikut sinkron.
                </div>
              </Form.Group>
            )}

            <Form.Group className="field-stack span-4">
              <Form.Label>{isRawMaterialWorkspace ? 'Kode bahan' : 'Kode produk'}</Form.Label>
              <Form.Control
                name="code"
                value={formData.code}
                onChange={handleFormChange}
                placeholder={isRawMaterialWorkspace ? 'Contoh: BB-001' : 'Contoh: PRD-001'}
                required
              />
            </Form.Group>

            <Form.Group className="field-stack span-8">
              <Form.Label>{isRawMaterialWorkspace ? 'Nama bahan' : 'Nama produk'}</Form.Label>
              <Form.Control
                name="name"
                value={formData.name}
                onChange={handleFormChange}
                placeholder={isRawMaterialWorkspace ? 'Nama bahan baku' : 'Nama yang tampil di POS'}
                required
              />
            </Form.Group>
          </div>
        </section>
      );
    }

    if (activeFormStep === 'pricing') {
      if (isRawMaterialWorkspace) {
        return (
          <section className="pos-catalog-form-section">
            <div className="pos-catalog-section-head">
              <div>
                <span className="section-badge">Langkah 2</span>
                <h2>Harga beli, stok, dan status</h2>
                <p>
                  Isi seperti cara supplier menjual bahan ini. Sistem akan langsung mengubah `KG/L/pack` menjadi stok dasar
                  dan menghitung HPP per `GRAM/ML/PCS` otomatis.
                </p>
              </div>
            </div>

            <div className="form-grid">
              <Form.Group className="field-stack span-4">
                <Form.Label>Satuan stok dasar</Form.Label>
                <Form.Control
                  name="satuan"
                  value={formData.satuan}
                  onChange={handleFormChange}
                  placeholder="GRAM / ML / PCS"
                />
                <div className="pos-catalog-field-note">
                  Stok akhir bahan selalu disimpan dalam unit terkecil yang benar-benar berkurang saat produksi.
                </div>
              </Form.Group>

              <Form.Group className="field-stack span-4">
                <Form.Label>Unit beli supplier</Form.Label>
                <Form.Control
                  name="purchase_unit"
                  value={formData.purchase_unit}
                  onChange={handleFormChange}
                  placeholder="KG / LITER / PACK"
                />
                <div className="pos-catalog-field-note">
                  Contoh: kopi dibeli dalam `KG`, susu dibeli dalam `LITER`.
                </div>
              </Form.Group>

              <Form.Group className="field-stack span-4">
                <Form.Label>Konversi otomatis</Form.Label>
                <Form.Control
                  name="purchase_conversion_qty"
                  type="number"
                  min="1"
                  value={formData.purchase_conversion_qty}
                  onChange={handleFormChange}
                  placeholder="1"
                />
                <div className="pos-catalog-field-note">
                  1 {formatInventoryUnitLabel(resolvedPurchaseUnit || 'UNIT BELI')} = ? {formatInventoryUnitLabel(formData.satuan)}
                </div>
              </Form.Group>

              <Form.Group className="field-stack span-4">
                <Form.Label>Total harga beli stok awal</Form.Label>
                <Form.Control
                  name="price"
                  type="number"
                  min="0"
                  value={formData.price}
                  onChange={handleFormChange}
                  placeholder="0"
                  required
                />
                <div className="pos-catalog-field-note">
                  Contoh: isi `777000` jika total harga stok awal `4,5 {formatInventoryUnitLabel(resolvedPurchaseUnit || 'KG')}` adalah Rp 777.000.
                </div>
              </Form.Group>

              <Form.Group className="field-stack span-4">
                <Form.Label>HPP otomatis / satuan stok</Form.Label>
                <Form.Control
                  value={formData.last_purchase_price}
                  disabled
                  placeholder="Dihitung otomatis"
                />
                <div className="pos-catalog-field-note">
                  Sistem menghitung otomatis `total harga beli / stok otomatis total`, lalu menghasilkan biaya per 1 {formatInventoryUnitLabel(formData.satuan)}.
                </div>
              </Form.Group>

              <Form.Group className="field-stack span-4">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  name="is_active"
                  value={formData.is_active ? 'active' : 'inactive'}
                  onChange={event =>
                    setFormData(prev => ({
                      ...prev,
                      is_active: event.target.value === 'active',
                    }))
                  }
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="field-stack span-6">
                <Form.Label>Stok awal / unit beli</Form.Label>
                <Form.Control
                  name="stock_reference_qty"
                  type="number"
                  min="0"
                  value={formData.stock_reference_qty}
                  onChange={handleFormChange}
                  placeholder="0"
                />
                <div className="pos-catalog-field-note">
                  Isi jumlah stok awal seperti kebiasaan beli. Contoh: `4,5` jika awalnya punya 4,5 {formatInventoryUnitLabel(resolvedPurchaseUnit || 'KG')}.
                </div>
              </Form.Group>

              <Form.Group className="field-stack span-6">
                <Form.Label>Stok otomatis / satuan stok</Form.Label>
                <Form.Control
                  value={formData.stock}
                  disabled
                  placeholder="Dihitung otomatis"
                />
                <div className="pos-catalog-field-note">
                  Sistem mengubah stok awal ke {formatInventoryUnitLabel(formData.satuan)} agar langsung siap dipakai di BOM.
                </div>
              </Form.Group>
            </div>

            <section className="pos-catalog-subsection">
              <div className="pos-catalog-section-head">
                <div>
                  <span className="section-badge">Hasil Otomatis</span>
                  <h2>Konversi dan biaya langsung terbaca</h2>
                  <p>
                    Ringkasan ini menunjukkan hasil akhir yang akan disimpan ke sistem, supaya staf tidak perlu menghitung manual.
                  </p>
                </div>
              </div>

              <div className="pos-catalog-inline-list">
                <article className="pos-catalog-inline-item pos-catalog-inline-item--soft">
                  <div className="pos-catalog-value-stack">
                    <span>Konversi stok</span>
                    <strong>
                      1 {formatInventoryUnitLabel(resolvedPurchaseUnit)} = {purchaseConversionQty.toLocaleString('id-ID')} {formatInventoryUnitLabel(formData.satuan)}
                    </strong>
                  </div>
                </article>
                <article className="pos-catalog-inline-item pos-catalog-inline-item--soft">
                  <div className="pos-catalog-value-stack">
                    <span>Harga otomatis</span>
                    <strong>
                      {formatCurrency(parseFlexibleNumber(formData.price || 0))} / {parseFlexibleNumber(formData.stock || 0).toLocaleString('id-ID')} {formatInventoryUnitLabel(formData.satuan)} = {formatInventoryCost(formData.last_purchase_price || 0)} / {formatInventoryUnitLabel(formData.satuan)}
                    </strong>
                  </div>
                </article>
                <article className="pos-catalog-inline-item pos-catalog-inline-item--soft">
                  <div className="pos-catalog-value-stack">
                    <span>Stok otomatis</span>
                    <strong>
                      {parseFlexibleNumber(formData.stock_reference_qty || 0).toLocaleString('id-ID')} {formatInventoryUnitLabel(resolvedPurchaseUnit)} = {parseFlexibleNumber(formData.stock || 0).toLocaleString('id-ID')} {formatInventoryUnitLabel(formData.satuan)}
                    </strong>
                  </div>
                </article>
              </div>
            </section>
          </section>
        );
      }

      return (
        <section className="pos-catalog-form-section">
          <div className="pos-catalog-section-head">
            <div>
              <span className="section-badge">
                {isRawMaterialWorkspace ? 'Langkah 2' : 'Langkah 3'}
              </span>
              <h2>{isRawMaterialWorkspace ? 'Harga beli, stok, dan status' : 'Harga, stok, dan status'}</h2>
              <p>
                {isRawMaterialWorkspace
                  ? 'Atur stok dasar bahan, unit beli default, dan biaya dasar per satuan stok.'
                  : 'Atur harga jual, stok, serta aturan biaya per satuan stok untuk produk yang memakai inventory.'}
              </p>
            </div>
          </div>

          <div className="form-grid">
            <Form.Group className="field-stack span-4">
              <Form.Label>{isRawMaterialWorkspace ? 'Harga referensi' : 'Harga jual'}</Form.Label>
              <Form.Control
                name="price"
                type="number"
                min="0"
                value={formData.price}
                onChange={handleFormChange}
                placeholder="0"
                required
              />
            </Form.Group>

            <Form.Group className="field-stack span-4">
              <Form.Label>
                {isRecipeEnabledItemType(formData.item_type)
                  ? 'HPP otomatis dari BOM'
                  : isRawMaterialWorkspace
                    ? 'HPP / satuan stok'
                    : 'HPP / satuan stok'}
              </Form.Label>
              <Form.Control
                name="last_purchase_price"
                type="number"
                min="0"
                value={
                  isRecipeEnabledItemType(formData.item_type)
                    ? String(effectiveLastPurchasePrice || 0)
                    : formData.last_purchase_price
                }
                onChange={handleFormChange}
                placeholder="0"
                disabled={isRecipeEnabledItemType(formData.item_type)}
              />
              <div className="pos-catalog-field-note">
                {isRecipeEnabledItemType(formData.item_type)
                  ? 'Produk finished good tidak input HPP manual. Nilai ini dijumlahkan otomatis dari recipe/BOM aktif.'
                  : `Isi biaya dasar per 1 ${formatInventoryUnitLabel(formData.satuan || 'satuan stok')}. Jika beli per KG/L, konversinya diatur di bawah.`}
              </div>
            </Form.Group>

            <Form.Group className="field-stack span-4">
              <Form.Label>Status</Form.Label>
              <Form.Select
                name="is_active"
                value={formData.is_active ? 'active' : 'inactive'}
                onChange={event =>
                  setFormData(prev => ({
                    ...prev,
                    is_active: event.target.value === 'active',
                  }))
                }
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="field-stack span-6">
              <Form.Label>Satuan stok dasar</Form.Label>
              <Form.Control
                name="satuan"
                value={formData.satuan}
                onChange={handleFormChange}
                placeholder="GRAM / ML / PCS"
              />
              <div className="pos-catalog-field-note">
                Simpan stok dalam unit terkecil yang benar-benar berkurang saat produksi, misalnya `GRAM`, `ML`, atau `PCS`.
              </div>
            </Form.Group>

            <Form.Group className="field-stack span-6">
              <Form.Label>Stok awal</Form.Label>
              <Form.Control
                name="stock"
                type="number"
                min="0"
                value={formData.stock}
                onChange={handleFormChange}
                placeholder={isRecipeEnabledItemType(formData.item_type) ? 'Stok dihitung dari bahan baku' : '0'}
                disabled={usesDerivedStock}
              />
              <div className="pos-catalog-field-note">
                {isRecipeEnabledItemType(formData.item_type)
                  ? 'Finished good tidak memakai stok manual. Konsumsi stok diturunkan dari bahan recipe/BOM.'
                  : formData.item_type === 'service'
                    ? 'Produk laundry/jasa tidak memakai stok product.'
                    : 'Koreksi stok lanjutan tetap lewat purchase, adjustment, atau opname.'}
              </div>
            </Form.Group>
          </div>

          {usesPurchasingConfig ? (
            <section className="pos-catalog-subsection">
              <div className="pos-catalog-section-head">
                <div>
                  <span className="section-badge">Flow Pembelian</span>
                  <h2>Unit beli ke stok dasar</h2>
                  <p>
                    Atur bagaimana supplier menjual bahan ini agar sistem bisa mengubah pembelian ke stok dasar otomatis.
                  </p>
                </div>
              </div>

              <div className="form-grid">
                <Form.Group className="field-stack span-4">
                  <Form.Label>Default unit beli</Form.Label>
                  <Form.Control
                    name="purchase_unit"
                    value={formData.purchase_unit}
                    onChange={handleFormChange}
                    placeholder="KG / LITER / PACK"
                  />
                </Form.Group>

                <Form.Group className="field-stack span-4">
                  <Form.Label>Konversi ke stok dasar</Form.Label>
                  <Form.Control
                    name="purchase_conversion_qty"
                    type="number"
                    min="1"
                    value={formData.purchase_conversion_qty}
                    onChange={handleFormChange}
                    placeholder="1"
                  />
                  <div className="pos-catalog-field-note">
                    Isi berapa {formatInventoryUnitLabel(formData.satuan || 'SATUAN STOK')} yang didapat dari 1 {formatInventoryUnitLabel(resolvedPurchaseUnit || 'UNIT BELI')}.
                  </div>
                </Form.Group>

                <div className="field-stack span-4">
                  <Form.Label>Preview konversi</Form.Label>
                  <div className="pos-catalog-inline-item pos-catalog-inline-item--soft">
                    <div className="pos-catalog-value-stack">
                      <strong>
                        1 {formatInventoryUnitLabel(resolvedPurchaseUnit || 'UNIT BELI')} = {purchaseConversionQty.toLocaleString('id-ID')} {formatInventoryUnitLabel(formData.satuan)}
                      </strong>
                      <small>
                        Purchase akan menaikkan stok dasar dan menghitung HPP dasar menjadi {formatInventoryCost(effectiveLastPurchasePrice)} / {formatInventoryUnitLabel(formData.satuan)}.
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          ) : null}

          {isRecipeEnabledItemType(formData.item_type) ? renderRecipeEditor() : null}
        </section>
      );
    }

    return (
      <section className="pos-catalog-form-section">
          <div className="pos-catalog-section-head">
            <div>
              <span className="section-badge">
                {isRawMaterialWorkspace ? 'Langkah 3' : 'Langkah 4'}
              </span>
              <h2>{isRawMaterialWorkspace ? 'Media dan preview bahan' : 'Media dan preview'}</h2>
              <p>
                {isRawMaterialWorkspace
                  ? 'Tambahkan gambar bila perlu agar bahan baku lebih mudah dikenali saat purchase dan audit stok.'
                  : 'Tambahkan gambar agar katalog lebih rapi dan staf kasir lebih mudah mengenali produk.'}
              </p>
            </div>
          </div>

        <div className="pos-catalog-product-preview">
          <div className="pos-catalog-product-preview__card">
            <span className="page-eyebrow">Preview cepat</span>
            <h3>{formData.name || (isRawMaterialWorkspace ? 'Nama bahan' : 'Nama produk')}</h3>
            <p>{formData.code || (isRawMaterialWorkspace ? 'Kode bahan belum diisi' : 'Kode produk belum diisi')}</p>
            <div className="pos-catalog-toolbar__meta">
              <span className={getItemTypeBadgeClass(formData.item_type)}>
                {getItemTypeLabel(formData.item_type)}
              </span>
              <span className={getBooleanStatusClass(Boolean(formData.is_active))}>
                {formData.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div className="pos-catalog-inline-list">
              <article className="pos-catalog-inline-item">
                <span>{isRawMaterialWorkspace ? 'Total harga stok awal' : 'Harga jual'}</span>
                <strong>
                  {isRawMaterialWorkspace
                    ? formatCurrency(formData.price || 0)
                    : formatCurrency(formData.price || 0)}
                </strong>
              </article>
              <article className="pos-catalog-inline-item">
                <span>{isRecipeEnabledItemType(formData.item_type) ? 'Recipe aktif' : 'Stok / satuan'}</span>
                <strong>
                  {isRecipeEnabledItemType(formData.item_type)
                    ? `${activeRecipeDraftCount || persistedRecipeCount} bahan BOM`
                    : isRawMaterialWorkspace
                      ? `${parseFlexibleNumber(formData.stock_reference_qty || 0).toLocaleString('id-ID')} ${formatInventoryUnitLabel(resolvedPurchaseUnit)} -> ${parseFlexibleNumber(formData.stock || 0).toLocaleString('id-ID')} ${formatInventoryUnitLabel(formData.satuan)}`
                      : `${Number(formData.stock || 0).toLocaleString('id-ID')} ${formatInventoryUnitLabel(formData.satuan)}`}
                </strong>
              </article>
              <article className="pos-catalog-inline-item">
                <span>{isRecipeEnabledItemType(formData.item_type) ? 'HPP otomatis' : 'HPP / stok dasar'}</span>
                <strong>
                  {isRecipeEnabledItemType(formData.item_type)
                    ? formatCurrency(effectiveLastPurchasePrice)
                    : `${formatInventoryCost(effectiveLastPurchasePrice)} / ${formatInventoryUnitLabel(formData.satuan)}`}
                </strong>
              </article>
              {usesPurchasingConfig ? (
                <article className="pos-catalog-inline-item">
                  <span>Unit beli default</span>
                  <strong>
                    1 {formatInventoryUnitLabel(resolvedPurchaseUnit)} = {purchaseConversionQty.toLocaleString('id-ID')} {formatInventoryUnitLabel(formData.satuan)}
                  </strong>
                </article>
              ) : null}
            </div>
          </div>

          <div className="pos-catalog-product-preview__media">
            <Form.Group className="field-stack">
              <Form.Label>{isRawMaterialWorkspace ? 'Gambar bahan' : 'Gambar produk'}</Form.Label>
              <Form.Control type="file" accept="image/*" onChange={handleImageChange} />
            </Form.Group>

            <div className="pos-catalog-image-preview">
              {imagePreviewUrl ? (
                <img src={imagePreviewUrl} alt={formData.name || (isRawMaterialWorkspace ? 'Preview bahan' : 'Preview produk')} />
              ) : (
                <div className="pos-catalog-image-preview__empty">
                  <FaImage />
                  <strong>Belum ada gambar</strong>
                  <span>
                    {isRawMaterialWorkspace
                      ? 'Upload gambar agar master bahan baku lebih mudah dikenali saat purchase dan audit stok.'
                      : 'Upload gambar agar katalog POS dan identitas produk lebih rapi.'}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    );
  };

  return (
    <>
      <Navigation />
      <Container fluid className="admin-page-shell mt-4 px-4">
        <AdminPageHeader
          icon={FaBoxOpen}
          eyebrow="Catalog & Inventory"
          title={pageTitle}
          description={pageDescription}
          breadcrumbs={[
            { label: 'Catalog & Inventory' },
            { label: pageTitle },
          ]}
          actions={headerActions}
          meta={headerMeta}
        />

        <div className="pos-catalog-shell">
          {!POS_API_BASE_URL ? (
            <Alert variant="danger" className="section-card">
              Endpoint POS belum diatur. Pastikan `REACT_APP_API_PRODUCT_URL` tersedia di konfigurasi `adminfitness`.
            </Alert>
          ) : null}

          {errorMessage ? (
            <Alert variant="warning" className="section-card">
              {errorMessage}
            </Alert>
          ) : null}

          <div className="pos-catalog-kpi-grid">
            <div className="surface-card pos-catalog-kpi-card">
              <span className="pos-catalog-kpi-card__icon">
                <FaBoxes />
              </span>
              <div className="pos-catalog-kpi-card__copy">
                <p>{isRawMaterialWorkspace ? 'Total bahan' : 'Total produk'}</p>
                <strong>{productSummary.total}</strong>
              </div>
            </div>

            <div className="surface-card pos-catalog-kpi-card">
              <span className="pos-catalog-kpi-card__icon">
                <FaCheckCircle />
              </span>
              <div className="pos-catalog-kpi-card__copy">
                <p>{isRawMaterialWorkspace ? 'Bahan aktif' : 'Produk aktif'}</p>
                <strong>{productSummary.active}</strong>
              </div>
            </div>

            <div className="surface-card pos-catalog-kpi-card">
              <span className="pos-catalog-kpi-card__icon">
                <FaWarehouse />
              </span>
              <div className="pos-catalog-kpi-card__copy">
                <p>Stok rendah</p>
                <strong>{productSummary.lowStock}</strong>
              </div>
            </div>

            <div className="surface-card pos-catalog-note-card">
              <span className="page-eyebrow">
                {isRawMaterialWorkspace ? 'Terhubung ke BOM' : 'Jual Dengan Bahan Baku'}
              </span>
              <h2>{productSummary.recipeEnabled}</h2>
              <p>
                {isRawMaterialWorkspace
                  ? 'Jumlah produk finished good yang saat ini sudah terhubung ke recipe/BOM aktif.'
                  : 'Produk bertipe POS Dengan Bahan Baku yang sudah terhubung ke recipe/BOM aktif.'}
              </p>
            </div>
          </div>

          <div className="surface-card pos-catalog-note-card">
            <span className="page-eyebrow">
              {isRawMaterialWorkspace ? 'Workspace Bahan Baku' : 'Alur Product Master'}
            </span>
            <h2>{isRawMaterialWorkspace ? 'Bahan baku dipisah dari produk jual' : 'Workspace produk bertahap'}</h2>
            <p>
              {isRawMaterialWorkspace
                ? 'User cukup fokus ke stok bahan, satuan, dan harga beli tanpa melihat mode produk lain di halaman ini.'
                : 'Untuk user pemula, input produk sekarang dipisah menjadi tab kerja dan langkah singkat supaya fokusnya tidak bercampur dengan tabel data.'}
            </p>
          </div>

          <section className="surface-card pos-catalog-workspace-card">
            <div className="pos-catalog-workspace-head">
              <div>
                <span className="page-eyebrow">
                  {isRawMaterialWorkspace ? 'Workspace Bahan Baku' : 'Workspace Product Master'}
                </span>
                <h2>
                  {activeWorkspaceTab === 'form'
                    ? isRawMaterialWorkspace
                      ? 'Input bahan baku bertahap'
                      : 'Input produk bertahap'
                    : isRawMaterialWorkspace
                      ? 'Daftar bahan baku outlet'
                      : 'Daftar produk outlet'}
                </h2>
                <p>
                  {activeWorkspaceTab === 'form'
                    ? isRawMaterialWorkspace
                      ? 'Ikuti langkah singkat ini agar staf fokus membuat bahan baku tanpa bercampur dengan mode produk jual.'
                      : 'Ikuti langkah per langkah agar staf baru lebih mudah membuat produk tanpa bingung melihat semua field sekaligus.'
                    : isRawMaterialWorkspace
                      ? 'Gunakan daftar ini untuk mencari bahan baku, cek stok, lalu lanjut edit bila perlu.'
                      : 'Gunakan daftar ini untuk mencari produk, cek tipe item, lalu lanjut edit bila perlu.'}
                </p>
              </div>
              <div className="pos-catalog-panel-tabs">
                {workspaceMenuItems.map(item => {
                  const TabIcon = item.icon;
                  return (
                    <button
                      key={item.key}
                      type="button"
                      className={`pos-catalog-panel-tab${item.active ? ' is-active' : ''}`}
                      onClick={() => {
                        if (item.key === 'product_list') {
                          navigateToWorkspace('products', 'list');
                          return;
                        }

                        if (item.key === 'raw_material') {
                          navigateToWorkspace('raw_material', 'list');
                          return;
                        }

                        navigateToWorkspace('products', 'form');
                      }}
                    >
                      <TabIcon />
                      <div className="pos-catalog-value-stack">
                        <strong>{item.label}</strong>
                        <small>{item.helper}</small>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {activeWorkspaceTab === 'form' ? (
              <div className="pos-catalog-product-workspace">
                <aside className="pos-catalog-product-stepper">
                  <div className="pos-catalog-stepper-card">
                    <span className="page-eyebrow">
                      {editingId
                        ? isRawMaterialWorkspace
                          ? 'Edit Bahan'
                          : 'Edit Produk'
                        : 'Form Baru'}
                    </span>
                    <h3>
                      {editingId
                        ? isRawMaterialWorkspace
                          ? 'Perbarui master bahan baku'
                          : 'Perbarui product master'
                        : isRawMaterialWorkspace
                          ? 'Tambah bahan baku'
                          : 'Tambah product master'}
                    </h3>
                    <p>
                      {isRawMaterialWorkspace
                        ? 'Mulai dari identitas bahan lalu lanjutkan ke stok, harga beli, dan preview.'
                        : 'Mulai dari mode produk lalu lanjutkan ke identitas, harga, dan preview.'}
                    </p>
                  </div>

                  <div className="pos-catalog-step-list">
                    {formSteps.map((step, index) => {
                      const isActive = step.key === activeFormStep;
                      const isDone = index < activeFormStepIndex;

                      return (
                        <button
                          key={step.key}
                          type="button"
                          className={`pos-catalog-step-item${isActive ? ' is-active' : ''}${isDone ? ' is-done' : ''}`}
                          onClick={() => setActiveFormStep(step.key)}
                        >
                          <span className="pos-catalog-step-item__index">{index + 1}</span>
                          <div className="pos-catalog-value-stack">
                            <small>{step.badge}</small>
                            <strong>{step.title}</strong>
                            <span>{step.helper}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  <div className="pos-catalog-stepper-card is-soft">
                    <span className="page-eyebrow">Langkah aktif</span>
                    <h3>{activeFormStepConfig.title}</h3>
                    <p>{activeFormStepConfig.helper}</p>
                  </div>
                </aside>

                <section className="pos-catalog-product-step-content">
                  <Form onSubmit={handleSubmit} className="pos-catalog-product-form">
                    {renderFormStepContent()}

                    <div className="pos-catalog-form-actions mt-3">
                      <div className="pos-catalog-form-actions__group">
                        <Button variant="outline-secondary" type="button" onClick={resetForm}>
                          Reset
                        </Button>
                        <Button
                          variant="outline-secondary"
                          type="button"
                          onClick={goToPreviousFormStep}
                          disabled={activeFormStepIndex === 0}
                        >
                          <FaArrowLeft /> Kembali
                        </Button>
                      </div>

                      <div className="pos-catalog-form-actions__group">
                        {activeFormStepIndex < formSteps.length - 1 ? (
                          <Button type="button" onClick={goToNextFormStep}>
                            Lanjut <FaArrowRight />
                          </Button>
                        ) : (
                          <Button type="submit" disabled={saving || !selectedOutlet || !POS_API_BASE_URL}>
                            {editingId ? <FaSave /> : <FaPlus />}
                            {saving
                              ? 'Menyimpan...'
                              : editingId
                                ? `Update ${workspaceEntityLabelTitle}`
                                : `Simpan ${workspaceEntityLabelTitle}`}
                          </Button>
                        )}
                      </div>
                    </div>
                  </Form>
                </section>
              </div>
            ) : (
              <section className="table-card pos-catalog-table-card">
                <div className="pos-catalog-toolbar">
                  <div>
                    <span className="page-eyebrow">
                      {isRawMaterialWorkspace ? 'Data Bahan Baku' : 'Data Produk'}
                    </span>
                    <h2 className="mb-1">
                      {isRawMaterialWorkspace ? 'Daftar bahan baku outlet' : 'Daftar produk outlet'}
                    </h2>
                    <p className="text-muted mb-0">
                      {isRawMaterialWorkspace
                        ? 'Cari bahan baku berdasarkan nama, kode, kategori, atau tipe item.'
                        : 'Cari produk berdasarkan nama, kode, kategori, atau tipe item.'}
                    </p>
                  </div>

                  <div className="pos-catalog-toolbar__meta">
                    {isRawMaterialWorkspace ? (
                      <Button type="button" onClick={openCreateRawMaterialForm}>
                        <FaPlus /> Tambah Bahan Baku
                      </Button>
                    ) : null}
                    <div className="search-control control-with-icon">
                      <span className="control-with-icon__icon">
                        <FaSearch />
                      </span>
                      <Form.Control
                        value={search}
                        onChange={event => setSearch(event.target.value)}
                        placeholder={isRawMaterialWorkspace ? 'Cari bahan baku' : 'Cari produk POS'}
                      />
                    </div>
                    <span className="pos-catalog-filter-chip">
                      {filteredProducts.length} dari {workspaceProducts.length} {isRawMaterialWorkspace ? 'bahan' : 'produk'}
                    </span>
                    <span className="pos-catalog-filter-chip">
                      {productSummary.active} aktif
                    </span>
                  </div>
                </div>

                <AdminDataTable
                  columns={columns}
                  data={filteredProducts}
                  progressPending={loading}
                  pagination
                  persistTableHead
                  emptyTitle={isRawMaterialWorkspace ? 'Belum ada bahan baku' : 'Belum ada produk POS'}
                  emptyDescription={
                    isRawMaterialWorkspace
                      ? 'Tambahkan bahan baku lebih dulu agar purchase, stok bahan, dan recipe/BOM bisa dibangun dengan benar.'
                      : 'Mulai dari product master agar purchase, item/variant, dan inventory POS bisa dibangun dengan benar.'
                  }
                  tableMinWidth="980px"
                />
              </section>
            )}
          </section>
        </div>
      </Container>
    </>
  );
}

export default PosProductsManager;
